import { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as DE from "@/data/content";
import * as EN from "@/data/content.en";
import { UI } from "./ui";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import { LanguageFlag } from "./LanguageFlag";
import "./language-picker.css";
import { setActiveLocale, translateText, translateTree } from "./translate";
const SUPPORTED = ["en", "de", "sv", "da", "fr", "nl", "no"];

const LangCtx = createContext({ lang: "de", setLang: () => {} });

const initialLang = () => {
  const param = new URLSearchParams(window.location.search).get("lang");
  if (SUPPORTED.includes(param)) return param;
  const stored = window.localStorage.getItem("sss-lang");
  if (SUPPORTED.includes(stored)) return stored;
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language || ""];
  const match = prefs.map((l) => l.toLowerCase().slice(0, 2)).map(l => l === "nb" || l === "nn" ? "no" : l).find((l) => SUPPORTED.includes(l));
  return match ?? "de";
};

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(initialLang);
  setActiveLocale(lang);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = translateText(UI[lang === "de" ? "de" : "en"].title, lang);
  }, [lang]);
  const value = useMemo(
    () => ({
      lang,
      setLang: (l) => {
        if (!SUPPORTED.includes(l)) return;
        window.localStorage.setItem("sss-lang", l);
        const url = new URL(window.location.href);
        if (url.searchParams.has("lang")) { url.searchParams.set("lang", l); window.history.replaceState(null, "", url); }
        setLang(l);
      },
    }),
    [lang]
  );
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);

export const useContent = () => {
  const { lang } = useLang();
  return useMemo(() => ({ ...translateTree(lang === "de" ? DE : EN, lang), ui: translateTree(UI[lang === "de" ? "de" : "en"], lang), lang }), [lang]);
};

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "sv", label: "Svenska" },
  { code: "da", label: "Dansk" },
  { code: "fr", label: "Français" },
  { code: "nl", label: "Nederlands" },
  { code: "no", label: "Norsk" },
];

export const LangSwitch = ({ className = "", testPrefix = "lang" }) => {
  const { lang, setLang } = useLang();
  const selected = LANGUAGES.find((language) => language.code === lang);
  return (
    <div className={`language-picker ${className}`}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <button type="button" className="language-trigger" aria-label={`${translateText(UI[lang === "de" ? "de" : "en"].langLabel, lang)}: ${selected.label}`} data-testid={`${testPrefix}-trigger`}>
            <LanguageFlag code={lang}/>
            <span className="language-current-name" lang={lang}>{selected.label}</span>
            <span className="language-current-code" aria-hidden="true">{lang.toUpperCase()}</span>
            <ChevronDown className="language-chevron" size={14} aria-hidden="true"/>
          </button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content className="language-dropdown" align="end" sideOffset={8} collisionPadding={12} aria-label={translateText(UI[lang === "de" ? "de" : "en"].langLabel, lang)}>
            <Dropdown.RadioGroup value={lang} onValueChange={setLang}>
              {LANGUAGES.map((language) => (
                <Dropdown.RadioItem key={language.code} value={language.code} className="language-option" lang={language.code} data-testid={`${testPrefix}-${language.code}`}>
                  <LanguageFlag code={language.code}/>
                  <span>{language.label}</span>
                  <Dropdown.ItemIndicator className="language-check"><Check size={16} aria-hidden="true"/></Dropdown.ItemIndicator>
                </Dropdown.RadioItem>
              ))}
            </Dropdown.RadioGroup>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </div>
  );
};
